import { showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'

import { rootApi } from 'root-api'

import {
  AVAILABILITY_EVENT_TYPES_TAG,
  AVAILABILITY_INVALIDATION_TAGS,
  AVAILABILITY_SCHEDULES_TAG,
  HOLIDAYS_PATH,
  USER_HOLIDAY_SETS_TAG,
  USER_SETTINGS_TAG
} from './constants'

const GET_AVAILABILITY_USER_HOLIDAY_SET = 'getAvailabilityUserHolidaySetV4'
const GET_AVAILABILITY_USER_EVENT_LIMITS = 'getAvailabilityUserEventLimits'

const api = rootApi.enhanceEndpoints({
  addTagTypes: ['AVAILABILITY_SCHEDULES', 'AVAILABILITY_EVENT_TYPES', 'USER_HOLIDAY_SETS', 'USER_SETTINGS', 'CURRENT_USER'],
}).injectEndpoints({
  endpoints: (build) => ({
    getAvailabilitySchedules: build.query({
      query: () => ({
        url: '/api/availability/schedules',
        method: 'GET',
      }),
      providesTags: [AVAILABILITY_SCHEDULES_TAG]
    }),
    getAvailabilityEventTypes: build.query({
      query: () => ({
        url: '/api/availability/event_types',
        method: 'GET',
      }),
      providesTags: [AVAILABILITY_EVENT_TYPES_TAG]
    }),
    [GET_AVAILABILITY_USER_HOLIDAY_SET]: build.query({
      query: () => ({
        url: '/api/availability/user_holiday_sets',
        validateStatus: (code) => code === 200 || code === 400,
      }),
      transformResponse: (response) => {
        if (response.holidays) return response
        return { uri: '', holidays: [] }
      },
      providesTags: [USER_HOLIDAY_SETS_TAG]
    }),
    [GET_AVAILABILITY_USER_EVENT_LIMITS]: build.query({
      query: () => ({
        url: '/api/availability/user_event_limits',
        method: 'GET',
      })
    }),
    getDefaultHolidaysV4: build.query({
      query: (countryCode) => ({
        url: '/api/availability/holidays',
        params: { country_code: countryCode },
      }),
    }),
    updateAvailabilityUserEventLimits: build.mutation({
      query: ({ eventLimits }) => ({
        url: '/api/availability/user_event_limits',
        method: 'PATCH',
        data: { eventLimits }
      }),
      invalidatesTags: ['CURRENT_USER'],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(api.util.updateQueryData(GET_AVAILABILITY_USER_EVENT_LIMITS, undefined, (draft) => {
            Object.assign(draft, arg.eventLimits, data)
          }))
          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
        } catch {
          // noop
        }
      },
    }),
    updateAvailabilityUserHolidaySetV4: build.mutation({
      query: ({ uuid, holidays }) => ({
        url: uuid
          ? `/api/availability/user_holiday_sets/${uuid}`
          : '/api/availability/user_holiday_sets',
        method: uuid ? 'PATCH' : 'POST',
        data: { holidays: holidays.map(({ key, enabled }) => ({ key, enabled })) },
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled, extra: { history } }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(api.util.updateQueryData(GET_AVAILABILITY_USER_HOLIDAY_SET, undefined, (draft) => {
            Object.assign(draft, data, { holidays: args.holidays })
          }))
          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
          if (args.redirect) {
            history.push(HOLIDAYS_PATH)
          }
        } catch {
          // noop
        }
      },
    }),
    updateAvailabilityLinkedEventTypes: build.mutation({
      query: ({ id, linked }) => ({
        url: `/api/availability/schedules/${id}/referencing_event_types`,
        method: 'POST',
        data: {
          event_type_ids: linked
        }
      }),
      invalidatesTags: (_, error) => (
        error ? [] : AVAILABILITY_INVALIDATION_TAGS
      ),
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
        },
      },
    }),
    getUserSettings: build.query({
      query: () => ({
        url: '/api/meetings/user_settings',
        method: 'GET',
      }),
      providesTags: [USER_SETTINGS_TAG],
      extraOptions: {
        handleErrors: false
      }
    }),
    patchUserSettings: build.mutation({
      query: ({ user_settings: userSettings, uuid, userUuid, skipToast }) => ({
        url: '/api/meetings/user_settings',
        method: 'PATCH',
        data: {
          ...(uuid || userUuid ? { uuid: uuid || userUuid } : {}),
          user_settings: userSettings,
        },
      }),
      invalidatesTags: (_, error) => (
        error ? [] : [USER_SETTINGS_TAG]
      ),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          api.util.updateQueryData('getUserSettings', undefined, (draft) => {
            if (args.user_settings.meetingsPageSettings) {
              draft.meetingsPageSettings = {
                ...draft.meetingsPageSettings,
                ...args.user_settings.meetingsPageSettings
              }
            }
            if (args.user_settings.productRefreshOptedIn !== undefined) {
              draft.productRefreshOptedIn = args.user_settings.productRefreshOptedIn
            }
          })
        )

        try {
          await queryFulfilled
          if (!args.skipToast) {
            dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
          }
        } catch {
          patchResult.undo()
        }
      },
    }),
    createAvailabilitySchedule: build.mutation({
      query: ({ name, rules, timezone }) => ({
        url: '/api/availability/schedules',
        method: 'POST',
        data: {
          name,
          rules,
          timezone,
          event_type_ids: []
        }
      }),
      invalidatesTags: (_, error) => (
        error ? [] : [AVAILABILITY_SCHEDULES_TAG]
      ),
      onQueryStarted: async ({ onSuccess }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled

          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
          if (onSuccess) {
            onSuccess(data)
          }
        } catch {
          // noop
        }
      }
    }),
    updateAvailabilitySchedule: build.mutation({
      query: ({ updatedSchedule }) => ({
        url: `/api/availability/schedules/${updatedSchedule.id}`,
        method: 'PATCH',
        data: updatedSchedule
      }),
      invalidatesTags: (_, error) => (
        error ? [] : [AVAILABILITY_SCHEDULES_TAG]
      ),
      onQueryStarted: async ({ onSuccess }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled

          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
          if (onSuccess) {
            onSuccess(data)
          }
        } catch {
          // noop
        }
      }
    }),
    setDefaultAvailabilitySchedule: build.mutation({
      query: ({ id }) => ({
        url: '/api/availability/schedules/primary_availability_rule',
        method: 'POST',
        data: { id }
      }),
      invalidatesTags: (_, error) => (
        error ? [] : [AVAILABILITY_SCHEDULES_TAG]
      ),
      onQueryStarted: async ({ onSuccess }, { dispatch, queryFulfilled, extra: { history } }) => {
        try {
          await queryFulfilled
          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
          if (onSuccess) { onSuccess() }
        } catch {
          // noop
        }
      }
    }),
    deleteAvailabilitySchedule: build.mutation({
      query: ({ id }) => ({
        url: `/api/availability/schedules/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (
        error ? [] : [AVAILABILITY_SCHEDULES_TAG]
      ),
      onQueryStarted: async ({ onSuccess }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
          dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
          if (onSuccess) { onSuccess() }
        } catch {
          // noop
        }
      },
    }),
  }),
})

export const {
  useCreateAvailabilityScheduleMutation,
  useDeleteAvailabilityScheduleMutation,
  useGetAvailabilityEventTypesQuery,
  useGetAvailabilitySchedulesQuery,
  useGetAvailabilityUserEventLimitsQuery,
  useGetAvailabilityUserHolidaySetV4Query,
  useGetUserSettingsQuery,
  useLazyGetDefaultHolidaysV4Query,
  usePatchUserSettingsMutation,
  useSetDefaultAvailabilityScheduleMutation,
  useUpdateAvailabilityLinkedEventTypesMutation,
  useUpdateAvailabilityScheduleMutation,
  useUpdateAvailabilityUserEventLimitsMutation,
  useUpdateAvailabilityUserHolidaySetV4Mutation,
} = api
